import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Avatar, Button, CircularProgress, Alert,
  TextField, Select, MenuItem, InputLabel, FormControl, Pagination, Dialog, DialogTitle, DialogContent, Tabs, Tab
} from '@mui/material';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState('username');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'search', 'date'
  const [selectedUser, setSelectedUser] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      setErrMsg('');
      try {
        const token = localStorage.getItem('token');
        let url = '';
        let options = { headers: { Authorization: `Bearer ${token}` } };
        if (filterMode === 'search' && searchValue) {
          url = `https://iconmarketing.shop/api/users/admin/search?page=${page}&&value=${searchValue}&&type=${searchType}`;
        } else if (filterMode === 'date' && startDate && endDate) {
          url = `https://iconmarketing.shop/api/users/filterUsersByDate/filter?page=${page}`;
          options = {
            ...options,
            method: 'POST',
            headers: { ...options.headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ startDate, endDate })
          };
        } else {
          url = `https://iconmarketing.shop/api/users/admin?page=${page}&users=all`;
        }
        const res = await fetch(url, options);
        const data = await res.json();
        if (res.ok && data.products) {
          setUsers(data.products);
          setTotalPages(data.totalPages || 5); // fallback if not provided
          setTotalResults(data.totalResults || data.totalCount || data.products.length);
        } else {
          setErrMsg(data.message || 'Failed to fetch users');
        }
      } catch (err) {
        setErrMsg('Network error');
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, [page, filterMode, searchValue, searchType, startDate, endDate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setFilterMode('search');
  };

  const handleDateFilter = (e) => {
    e.preventDefault();
    if (startDate && endDate) {
      setPage(1);
      setFilterMode('date');
    }
  };

  const handleClearFilters = () => {
    setSearchValue('');
    setStartDate('');
    setEndDate('');
    setFilterMode('all');
    setPage(1);
  };

  const handleRowClick = (user) => {
    setSelectedUser(user);
    setTabIndex(0);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ccc', pb: 1, marginBottom: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Users</Typography>
          <Button variant="outlined" color="primary" onClick={() => navigate('/promotions')}>Promotions</Button>
        </Box>
        <Typography variant="h4" color="primary">IconMarketing FrontDesk</Typography>
        <Button variant="outlined" color="error" onClick={handleLogout}>Logout</Button>
      </Box>
  {/* Search & Filter Controls */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2,display: 'flex', justifyContent: 'space-evenly', alignItems: 'center',}}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="search-type-label">Type</InputLabel>
            <Select
              labelId="search-type-label"
              value={searchType}
              label="Type"
              onChange={e => setSearchType(e.target.value)}
            >
              <MenuItem value="username">Username</MenuItem>
              <MenuItem value="reference">Reference</MenuItem>
              <MenuItem value="placement">Placement</MenuItem>
              <MenuItem value="phone">Phone</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search Value"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
          />
          <Button type="submit" variant="contained" color="primary" size="small">Search</Button>
        </form>
        <form onSubmit={handleDateFilter} style={{ display: 'flex', gap: 8 }}>
          <TextField
            size="small"
            label="Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
          <TextField
            size="small"
            label="End Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
          <Button type="submit" variant="contained" color="secondary" size="small">Date Search</Button>
        </form>
        <Button variant="outlined" size="small" onClick={handleClearFilters}>Show All</Button>
      </Box>
      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : errMsg ? (
        <Alert severity="error">{errMsg}</Alert>
      ) : (
        <>
          
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Photo</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Placement</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user._id} hover style={{ cursor: 'pointer' }} onClick={() => handleRowClick(user)}>
                    <TableCell>
                      <Avatar src={user.profilePhoto} alt={user.name} />
                    </TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>{user.gender}</TableCell>
                    <TableCell>{user.reference}</TableCell>
                    <TableCell>{user.placement}</TableCell>
                    <TableCell>{user.availablebalance}</TableCell>
                    <TableCell>{user.status ? 'Active' : 'Inactive'}</TableCell>
                    <TableCell>{user.createdBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, textAlign: 'left', color: 'text.secondary' }}>
            Showing {users.length} users on this page. Total found: {totalResults}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
          {/* User Details Modal */}
          <Dialog open={!!selectedUser} onClose={() => setSelectedUser(null)} maxWidth="md" fullWidth>
            <DialogTitle>User Details</DialogTitle>
            <DialogContent>
              <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 2 }}>
                <Tab label="Profile" />
                <Tab label="Account" />
                <Tab label="Other" />
              </Tabs>
              {selectedUser && tabIndex === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Avatar src={selectedUser.profilePhoto} alt={selectedUser.name} sx={{ width: 80, height: 80 }} />
                    <Typography variant="h6">{selectedUser.name}</Typography>
                  </Box>
                  <Typography variant="body2">Username: {selectedUser.username}</Typography>
                  <Typography variant="body2">Phone: {selectedUser.phone}</Typography>
                  <Typography variant="body2">Gender: {selectedUser.gender}</Typography>
                  <Typography variant="body2">Email: {selectedUser.email}</Typography>
                </Box>
              )}
              {selectedUser && tabIndex === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">Reference: {selectedUser.reference}</Typography>
                  <Typography variant="body2">Placement: {selectedUser.placement}</Typography>
                  <Typography variant="body2">Balance: {selectedUser.availablebalance}</Typography>
                  <Typography variant="body2">Status: {selectedUser.status ? 'Active' : 'Inactive'}</Typography>
                  <Typography variant="body2">Created By: {selectedUser.createdBy}</Typography>
                  <Typography variant="body2">Password: {selectedUser.password}</Typography>
                </Box>
              )}
              
              {selectedUser && tabIndex === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(selectedUser).map(([key, value]) => (
                    [
                      'profilePhoto','name','username','phone','gender','email',
                      'reference','placement','availablebalance','status','createdBy','password',
                      '_id','createdAt','lastLogin'
                    ].includes(key) ? null : (
                      <Box key={key} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>{key}:</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{String(value)}</Typography>
                      </Box>
                    )
                  ))}
                </Box>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </Container>
  );
}